import type { HealthHistoryResponse } from '@ofmos/shared/health-history';
import { RECORD_QUERY_MAX_LIMIT } from '@ofmos/shared/record-query';
import { hasFlag } from '@ofmos/shared/roles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppClient } from '../../../data/ClientProvider';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../../data/client';
import { useFields, useListedRecords, useObjects, useRecordList } from '../../../data/hooks';
import { queryKeys } from '../../../data/queryKeys';
import { useSession } from '../../auth/useSession';
import { ALL_OWNERS, OWNER_STORAGE_KEY, ownerStartFor } from './ownerMenu';

/**
 * THE SHARED READS AND STATE OF THE REDESIGNED PAGES (brief FE-FOUNDATION sections 10, 11, 15).
 * Objects and fields resolve BY API NAME against runtime metadata, and a missing one makes its
 * part absent rather than a crash, as every fulfillment page does.
 */

/** The object with this api name, or undefined in an installation without it. */
export function objectNamed(
  objects: readonly ObjectMetadata[] | undefined,
  name: string,
): ObjectMetadata | undefined {
  return objects?.find((object) => object.name === name);
}

/** One object and its fields by api name: `{ object, field(name) }`, both possibly undefined. */
export function useNamedFields(objectName: string): {
  readonly object: ObjectMetadata | undefined;
  readonly fields: readonly FieldMetadata[];
  readonly field: (name: string) => FieldMetadata | undefined;
} {
  const objects = useObjects();
  const object = objectNamed(objects.data, objectName);
  const fields = useFields(object?.id ?? '', object !== undefined);
  const list = fields.data ?? [];
  const field = useCallback(
    (name: string) => (fields.data ?? []).find((entry) => entry.name === name),
    [fields.data],
  );
  return { object, fields: list, field };
}

/**
 * THE DATA AGES (redesign D9): `GET /api/feeds/status`, readable by every signed-in person.
 * One cache entry, so the Dashboard's line, the Onboarding title and the stale notice agree.
 */
export function useFeedsStatus() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.feedsStatus(),
    queryFn: () => client.feeds.status(),
    staleTime: 60_000,
  });
}

/** "Retry now" is for a person holding `HEALTH_MANAGE` (redesign D10, record 10). */
export function useCanRetryTouch(): boolean {
  const session = useSession();
  const permissions = session.data?.permissions;
  return permissions !== undefined && hasFlag(permissions, 'HEALTH_MANAGE');
}

/**
 * "RETRY NOW": queues one touch run, then re-reads the feed status. Nothing claims the run
 * happened (record 10): the answer only says it was queued.
 */
export function useRetryTouch() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.feeds.retryTouch(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.feedsStatus() }),
  });
}

/** THE COLOUR HISTORY (redesign D10): per day since `since`, for `owner` (`all` or an id). */
export function useHealthHistory(since: string, owner: string = ALL_OWNERS) {
  const client = useAppClient();
  return useQuery<HealthHistoryResponse>({
    queryKey: queryKeys.healthHistory(since, owner),
    queryFn: () =>
      client.fulfillment.healthHistory(owner === ALL_OWNERS ? { since } : { since, owner }),
    staleTime: 5 * 60_000,
  });
}

const NO_IDS: readonly string[] = [];

/**
 * THE SIGNED-IN PERSON'S TEAM MEMBER, or null (moved here from the Onboarding board's own
 * resolution, `useOnboardingBoard.ts:161`): the active `team_member` whose e-mail is the
 * session's. "My clients" exists only when this is found.
 */
export function useMyTeamMemberId(): string | null {
  const session = useSession();
  const team = useNamedFields('team_member');
  const email = team.field('email');
  const active = team.field('is_active');
  const list = useRecordList(team.object?.id ?? '', {
    select: [email?.id, active?.id].filter((id): id is string => id !== undefined),
    limit: RECORD_QUERY_MAX_LIMIT,
    enabled: team.object !== undefined && email !== undefined,
  });
  /* One cache subscription for the list, not one observer per member (BR-PERF): the owner
     menu is on the Clients list, whose rows are read the same way (`useListedRecords`). */
  const members = useListedRecords(team.object?.id ?? '', list.data?.ids ?? NO_IDS, list);
  const mine = session.data?.email?.toLowerCase() ?? null;
  return useMemo(() => {
    if (mine === null || email === undefined) return null;
    const match = members.records.find((row: RecordRow) => {
      const value = row.values[email.id];
      const live = active === undefined || row.values[active.id] !== false;
      return live && typeof value === 'string' && value.toLowerCase() === mine;
    });
    return match?.id ?? null;
  }, [members.records, email, active, mine]);
}

/**
 * NOTHING FOR A LOAD UNDER A SECOND, THEN THE SKELETON (handbook I2 and H9, R46: "Loads under 1
 * second show nothing"). True once `active` has stayed true for `delayMs`; false again the moment
 * it ends. The one copy: every page that delays its skeleton reads it from here.
 */
export const SKELETON_DELAY_MS = 1000;

export function useDelayed(active: boolean, delayMs: number = SKELETON_DELAY_MS): boolean {
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (!active) {
      setLate(false);
      return;
    }
    const timer = setTimeout(() => setLate(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
  return active && late;
}

function readOwner(): string | null {
  try {
    return globalThis.localStorage?.getItem(OWNER_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

/**
 * THE OWNER CHOICE, REMEMBERED PER BROWSER under `fulfillment.owner`, the key the Dashboard,
 * Onboarding and Clients already share. Storage that throws (a private window) keeps the
 * choice for the page only. Whether the stored value is still an option is the menu's to say
 * (`resolveOwnerChoice`). Before a person has chosen, the menu starts where their role does
 * (`ownerStartFor`, decision page item 13.1).
 */
export function useOwnerChoice(): readonly [string, (next: string) => void] {
  const session = useSession();
  const [stored, setValue] = useState<string | null>(readOwner);
  const value = stored ?? ownerStartFor(session.data?.roles);
  const set = useCallback((next: string) => {
    setValue(next);
    try {
      globalThis.localStorage?.setItem(OWNER_STORAGE_KEY, next);
    } catch {
      /* The choice still holds for this page; it just is not remembered. */
    }
  }, []);
  return [value, set] as const;
}

/**
 * ROWS A PERSON JUST ACTED ON, HIDDEN AT ONCE (D2 "The page hides the row at once"; A13).
 * A page's list reads its rows from a list query that only the next read refreshes, so the
 * row would stay until then; this set leaves it out immediately, and Undo puts it back.
 */
export interface HiddenRows {
  readonly hidden: ReadonlySet<string>;
  readonly hide: (id: string) => void;
  readonly show: (id: string) => void;
}

export function useHiddenRows(): HiddenRows {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const hide = useCallback((id: string) => {
    setHidden((current) => (current.has(id) ? current : new Set([...current, id])));
  }, []);
  const show = useCallback((id: string) => {
    setHidden((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);
  return { hidden, hide, show };
}
